/**
 * A141 Phase 3 — PM portal · Agency Team page.
 *
 * Route: /property-manager/team
 * Access: any active agency user (admin or staff). Admin sees invite
 * form + per-row Deactivate; staff sees the same list read-only.
 *
 * All mutations go through /api/partner/agency-users/* which reuses
 * the Phase 2 primitives (`agency_users_service`) so the same
 * last-admin guard, paused-agency gate, audit log and email template
 * apply. Promote/demote is deliberately NOT exposed here — Aware
 * admin retains that surface in v1.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, LogOut, Users, UserPlus, UserMinus, ShieldCheck, RefreshCcw, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { formatDateAU } from "@/lib/formatDateAU";

export default function PropertyManagerTeam() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("agency_staff");

  const isAdmin = data?.viewer_agency_role === "agency_admin";

  const load = async () => {
    setError(null);
    try {
      const res = await api.get("/partner/agency-users");
      setData(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not load team";
      setError(detail);
    }
  };

  useEffect(() => { load(); }, []);

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !inviteName.trim()) {
      toast.error("Email and full name are required");
      return;
    }
    setBusy("invite");
    try {
      const res = await api.post("/partner/agency-users/invite", {
        email: inviteEmail.trim(),
        full_name: inviteName.trim(),
        agency_role: inviteRole,
      });
      if (res.data.send_status === "sent") {
        toast.success(`Invite email sent to ${res.data.user.email}`);
      } else {
        toast.warning(`User created but email ${res.data.send_status}: ${res.data.send_error || "unknown"}`);
      }
      setInviteEmail("");
      setInviteName("");
      setInviteRole("agency_staff");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not invite user");
    } finally {
      setBusy(null);
    }
  };

  const deactivate = async (targetUser) => {
    if (!window.confirm(
      `Deactivate ${targetUser.full_name || targetUser.email}?\n\n` +
      `They will lose access to the portal on their next request. ` +
      `Their historical bookings and reports remain intact.`
    )) return;
    setBusy(targetUser.id);
    try {
      await api.post(`/partner/agency-users/${targetUser.id}/deactivate`);
      toast.success(`${targetUser.full_name || targetUser.email} deactivated`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not deactivate user");
    } finally {
      setBusy(null);
    }
  };

  const onLogout = async () => { await logout(); window.location.assign("/"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          <BrandLogo to="/property-manager" size="sm" />
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden sm:inline">{user?.email}</span>
            <button
              onClick={onLogout}
              data-testid="pm-team-logout-btn"
              className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <Link
          to="/property-manager"
          data-testid="pm-team-back-link"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-900"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Managed Properties
        </Link>

        <div className="mt-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">
              <Users className="w-3.5 h-3.5" /> Agency Team
            </div>
            <h1
              data-testid="pm-team-h1"
              className="font-display mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight"
            >
              Your agency&apos;s team
            </h1>
            <p className="mt-2 text-slate-600 text-sm sm:text-base max-w-2xl">
              {isAdmin
                ? "Invite colleagues to help manage properties, bookings and reports. Agency admins can invite and deactivate users. Historical records created by any user remain intact for the whole team."
                : "The people in your agency who can access this portal. Ask an agency admin if you need to invite someone new."}
            </p>
          </div>
        </div>

        {error && (
          <div
            data-testid="pm-team-error"
            className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-3 flex items-center gap-2 text-sm text-rose-800"
          >
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* Admin-only invite form */}
        {isAdmin && (
          <section
            data-testid="pm-team-invite-section"
            className="mt-6 rounded-2xl border border-cyan-200 bg-cyan-50/40 p-5"
          >
            <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-800 flex items-center gap-1.5">
              <UserPlus className="w-3.5 h-3.5" /> Invite a colleague
            </div>
            <form
              onSubmit={submitInvite}
              className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_150px_auto] gap-2"
            >
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="pm-team-invite-email"
                required
                placeholder="colleague@your-agency.com"
                className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-white"
              />
              <input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                data-testid="pm-team-invite-name"
                required
                placeholder="Full name"
                className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-white"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                data-testid="pm-team-invite-role"
                className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-white"
              >
                <option value="agency_staff">Agency Staff</option>
                <option value="agency_admin">Agency Admin</option>
              </select>
              <button
                type="submit"
                disabled={busy !== null}
                data-testid="pm-team-invite-submit"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50 whitespace-nowrap"
              >
                <UserPlus className="w-4 h-4" /> Invite
              </button>
            </form>
          </section>
        )}

        {/* User list */}
        <section
          data-testid="pm-team-list"
          className="mt-6 space-y-2"
        >
          {data === null && (
            <div className="text-sm text-slate-500">Loading…</div>
          )}
          {data && data.users.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              No team members yet.
            </div>
          )}
          {data?.users.map((u) => {
            const isDisabled = u.status === "disabled" || u.disabled_at;
            const isRowAdmin = u.agency_role === "agency_admin";
            const isPrimary = u.id === data.primary_user_id;
            const isSelf = u.id === user?.id;
            const canDeactivate = isAdmin && !isSelf && !isDisabled;
            const rowBusy = busy === u.id;

            return (
              <div
                key={u.id}
                data-testid={`pm-team-row-${u.id}`}
                className={`rounded-xl border p-4 flex items-center justify-between gap-3 flex-wrap ${
                  isDisabled ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-slate-900">
                      {u.full_name || u.email}
                      {isSelf && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-cyan-700">You</span>}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      isRowAdmin ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-700"
                    }`} data-testid={`pm-team-role-${u.id}`}>
                      {isRowAdmin && <ShieldCheck className="w-3 h-3" />}
                      {isRowAdmin ? "Agency admin" : "Agency staff"}
                    </span>
                    {isPrimary && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800">
                        Primary
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      u.status === "invited" ? "bg-cyan-100 text-cyan-800" :
                      u.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                      u.status === "disabled" ? "bg-rose-100 text-rose-800" :
                      "bg-slate-100 text-slate-600"
                    }`} data-testid={`pm-team-status-${u.id}`}>
                      {u.status}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5 truncate">{u.email}</div>
                  {u.status === "invited" && u.last_invite_sent_at && (
                    <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
                      <RefreshCcw className="w-3 h-3" />
                      Invite sent {formatDateAU(u.last_invite_sent_at)}
                    </div>
                  )}
                  {isDisabled && u.disabled_at && (
                    <div className="text-[11px] text-rose-700 mt-0.5">
                      Deactivated {formatDateAU(u.disabled_at)}
                    </div>
                  )}
                </div>
                {canDeactivate && (
                  <button
                    type="button"
                    disabled={rowBusy}
                    onClick={() => deactivate(u)}
                    data-testid={`pm-team-deactivate-${u.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-rose-50 border border-rose-200 text-rose-700 hover:border-rose-500 disabled:opacity-40"
                  >
                    <UserMinus className="w-3.5 h-3.5" /> Deactivate
                  </button>
                )}
              </div>
            );
          })}
        </section>

        {!isAdmin && data && (
          <div
            data-testid="pm-team-staff-notice"
            className="mt-5 rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-600"
          >
            You&apos;re signed in as an <strong>Agency Staff</strong> member. Only agency admins can invite or deactivate users. Ask your primary agency admin (marked <em>Primary</em>) if you need to add or remove someone.
          </div>
        )}
      </main>
    </div>
  );
}
